/**
 * voice-v8 — das Ja/Nein-Knopfpaar der Rückfrage-Karten (22.10.2026). EINE Stelle für Trainingsplan (karten/training.ts) und
 * Shorts (karten/shorts.ts) — vorher stand dieselbe Sende-Logik in beiden Karten als Kopie.
 *
 * Die Knöpfe schicken nur einen FESTEN Wortlaut über den vorhandenen Rückkanal der Karten (`window.__voiceV8SendFromCard`); kein neuer
 * Schreibweg, kein Markup (alles textContent). Ein Tipp sperrt beide Knöpfe; kam die Nachricht nicht an, werden sie wieder frei.
 * Nach der Frist (`bisMs`, beim Nachladen eines alten Verlaufs) sind sie gesperrt; `gesperrtSatz` sperrt sie von Anfang an.
 * Nur ein ECHTER Tipp (`isTrusted`) sendet — ein Skript-Klick (Chrome-Erweiterung, browser_click auf die App) nie.
 *
 * 22.10.2026 (Martin 16:42): kam das Ja an, aber der Server löste den Vorschlag NICHT ein (Ja-Tor: fremder Text im Zug), meldet das
 * Ergebnis es per `meldeNichtEingeloest` (karten/knopf-rueckfrage.ts `meldeKnopfStand`). Dann wird die Karte wieder tippbar — erst NACH
 * dem abgeschlossenen Senden (ein Doppeltipp bleibt gesperrt), nie nach der Frist, nie nach einem Nein. Das Ja-Tor ändert das nicht:
 * ein neuer Tipp schickt nur wieder den festen Wortlaut dieser Karte.
 *
 * 27.10.2026 (Mandats-Karte, karten/prozess.ts): dieselbe Logik für eine Reihe aus MEHREREN Knöpfen (`haengeKnopfReiheAn`) — drei
 * Stufen-Knöpfe und ein Nein. Das Paar ist der Sonderfall mit zwei.
 */
import { absatz, aktionsKnopf, aktionsLeiste } from "../karte.js";

export interface KnopfWahl {
  /** Der feste Wortlaut, der als Martins Nachricht geht. */
  nachricht: string;
  /** Der Name für Screenreader. */
  name: string;
  /** Die Zeile nach dem Senden. */
  gesendet: string;
}

export interface KnopfPaar {
  ja: KnopfWahl;
  nein: KnopfWahl;
  /** Ende der Frist in ms seit 1970 — `null`: keine Frist bekannt, der Server entscheidet. */
  bisMs: number | null;
  /** Der Satz nach der Frist. */
  abgelaufenSatz: string;
  /** Gesetzt: die Knöpfe könnten nicht einlösen — von Anfang an gesperrt, mit diesem Satz. */
  gesperrtSatz?: string;
  /** Die Kennung des Vorschlags — nur damit hört die Karte auf „nicht eingelöst“ (`meldeNichtEingeloest`). */
  vorschlagId?: string;
}

/** Die Zeile, wenn das Ja ankam, der Vorschlag aber nicht eingelöst wurde — die Knöpfe sind wieder frei. */
export const NICHT_AUSGEFUEHRT_SATZ = "Nicht ausgeführt – bitte noch einmal tippen.";

/** Das Ereignis, mit dem ein Werkzeug-Ergebnis seiner Karte sagt: dein Ja wurde nicht eingelöst. */
const NICHT_EINGELOEST = "vespera:knopf-nicht-eingeloest";

interface NichtEingeloest {
  vorschlagId: string;
  /** Die Frist laut Server — `null`: unbekannt (dann gilt die der Karte). */
  bisMs: number | null;
}

/** Sagt der Karte mit dieser Kennung: ihr Ja kam an, wurde aber nicht eingelöst; der Vorschlag gilt (bis `bisMs`) noch. */
export function meldeNichtEingeloest(vorschlagId: string, bisMs: number | null): void {
  document.dispatchEvent(new CustomEvent<NichtEingeloest>(NICHT_EINGELOEST, { detail: { vorschlagId, bisMs } }));
}

/** Die Frist aus einem Ergebnisfeld (`gueltig_bis_ms`) — `null`, wenn sie fehlt oder keine Zahl ist. */
export function fristAus(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Ein Knopf einer Reihe: Wortlaut, Beschriftung, ob er ein Ja ist (nur Ja-Knöpfe hören auf „nicht eingelöst“). */
export interface KnopfEintrag extends KnopfWahl {
  /** Das sichtbare Wort. */
  text: string;
  /** Eine zweite, leise Zeile im Knopf (etwa „bis 3 €“). */
  unter?: string;
  primaer?: boolean;
  ja: boolean;
}

export interface KnopfReihe extends Omit<KnopfPaar, "ja" | "nein"> {
  knoepfe: readonly KnopfEintrag[];
  /** Eine Klasse für die Knopfreihe (Gestalt in style.css). */
  reiheKlasse?: string;
}

/** Hängt Knopfreihe und Standzeile an die Karte. */
export function haengeKnopfPaarAn(el: HTMLElement, o: KnopfPaar): void {
  const { ja, nein, ...rest } = o;
  haengeKnopfReiheAn(el, {
    ...rest,
    knoepfe: [
      { ...ja, text: "Ja", primaer: true, ja: true },
      { ...nein, text: "Nein", ja: false },
    ],
  });
}

/** Hängt eine Reihe fester Knöpfe und die Standzeile an die Karte. Ein Tipp sperrt alle. */
export function haengeKnopfReiheAn(el: HTMLElement, o: KnopfReihe): void {
  const abgelaufen = (t: number): boolean => o.bisMs !== null && t >= o.bisMs;
  const reihe = aktionsLeiste(el);
  if (o.reiheKlasse) reihe.classList.add(o.reiheKlasse);
  const zeile = absatz("", "hint");
  const knoepfe: (HTMLAnchorElement | HTMLButtonElement)[] = [];
  const frei = (an: boolean): void => {
    for (const k of knoepfe) (k as HTMLButtonElement).disabled = !an;
  };
  const sperre = (satz: string): void => {
    frei(false);
    zeile.textContent = satz;
  };
  /** Das letzte Ja: „unterwegs“ (Senden läuft), „gesendet“ (angekommen) — nur dann hört die Karte auf „nicht eingelöst“. */
  let ja: "keins" | "unterwegs" | "gesendet" = "keins";
  /** „Nicht eingelöst“ kam, während das Ja noch unterwegs war — frei erst, wenn das Senden abgeschlossen ist. */
  let nichtEingeloest = false;
  const wiederFrei = (): void => {
    ja = "keins";
    nichtEingeloest = false;
    if (abgelaufen(Date.now())) return sperre(o.abgelaufenSatz);
    frei(true);
    zeile.textContent = NICHT_AUSGEFUEHRT_SATZ;
  };
  if (o.vorschlagId !== undefined && o.gesperrtSatz === undefined) {
    let warImVerlauf = false;
    const hoere = (ev: Event): void => {
      // Die Karte stand im Verlauf und ist nicht mehr da: nicht weiter zuhören.
      if (el.isConnected) warImVerlauf = true;
      else if (warImVerlauf) return document.removeEventListener(NICHT_EINGELOEST, hoere);
      const d = (ev as CustomEvent<NichtEingeloest>).detail;
      if (!d || d.vorschlagId !== o.vorschlagId || ja === "keins") return;
      if (d.bisMs !== null && Date.now() >= d.bisMs) {
        ja = "keins";
        return sperre(o.abgelaufenSatz);
      }
      if (ja === "unterwegs") nichtEingeloest = true;
      else wiederFrei();
    };
    document.addEventListener(NICHT_EINGELOEST, hoere);
  }
  const tippe = (w: KnopfEintrag): void => {
    const senden = (window as unknown as { __voiceV8SendFromCard?: (t: string, b?: string) => Promise<boolean | void> | void }).__voiceV8SendFromCard;
    if (!senden || o.gesperrtSatz !== undefined || knoepfe.some((k) => (k as HTMLButtonElement).disabled)) return;
    if (abgelaufen(Date.now())) return sperre(o.abgelaufenSatz);
    sperre("Wird gesendet …");
    const istJa = w.ja;
    ja = istJa ? "unterwegs" : "keins";
    nichtEingeloest = false;
    const zurueck = (): void => {
      ja = "keins";
      frei(true);
      zeile.textContent = "Nicht gesendet — bitte noch einmal tippen.";
    };
    const angekommen = (): void => {
      if (istJa && nichtEingeloest) return wiederFrei();
      // Während des Sendens als abgelaufen gemeldet: der Abgelaufen-Satz bleibt stehen.
      if (istJa && ja === "keins") return;
      if (istJa) ja = "gesendet";
      zeile.textContent = w.gesendet;
    };
    Promise.resolve(senden(w.nachricht)).then((ok) => (ok === false ? zurueck() : angekommen()), zurueck);
  };
  // 27.10.2026 (Prüfung Prozess-Freigabe): nur ein ECHTER Tipp zählt (`isTrusted`, wie lib/auftrags-karte.ts). Die Chrome-Erweiterung klickt
  // mit `el.click()` auch in der App, wenn sie der aktive Tab ist — ein solcher Skript-Klick schickt kein Knopf-Ja.
  const wahl = (w: KnopfEintrag) => (ev: MouseEvent): void => {
    if (ev.isTrusted === true) tippe(w);
  };
  for (const w of o.knoepfe) {
    const k = aktionsKnopf({ text: w.text, ...(w.primaer ? { primaer: true } : {}), name: w.name, onClick: wahl(w) });
    if (w.unter) {
      const u = document.createElement("span");
      u.className = "tool-action-unter";
      u.textContent = w.unter;
      k.appendChild(u);
    }
    knoepfe.push(k);
  }
  reihe.append(...knoepfe);
  if (abgelaufen(Date.now())) sperre(o.abgelaufenSatz);
  else if (o.gesperrtSatz !== undefined) sperre(o.gesperrtSatz);
  el.appendChild(zeile);
}
